import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { BlurFade } from '@/components/ui/blur-fade'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import ImageLoading from '@/components/ui/image-loading'
import { Button } from '@/components/ui/button'
import { IconDownload, IconMaximize, IconVolume, IconVolumeOff } from '@tabler/icons-react'
import { ManimAnimationSkeleton } from '@/components/skeletons/manim-animation-skeleton'

interface AnimatedMarkdownVideoProps {
  src: string
  alt: string
  isStreaming: boolean
}

export const AnimatedMarkdownVideo: React.FC<AnimatedMarkdownVideoProps> = ({
  src,
  alt,
  isStreaming
}) => {
  const [showVideo, setShowVideo] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [processedSrc, setProcessedSrc] = useState<string>(src)
  const [isPlaying, setIsPlaying] = useState(false)

  // Extract file extension from src
  const fileExtension = src.split('.').pop()?.toLowerCase()
  const isGif = fileExtension === 'gif' || src.match(/\.gif(\?|$)/)
  const isVideoFile =
    fileExtension === 'mp4' ||
    fileExtension === 'webm' ||
    fileExtension === 'mov' ||
    src.includes('replicate.delivery') ||
    src.includes('anim.losi.one') || // Always treat animation API links as videos
    src.includes('mapi.losi.one') ||
    src.match(/\.(mp4|webm|mov)(\?|$)/)

  // Check if this is a Manim animation video
  const isManimAnimation =
    src.includes('anim.losi.one') ||
    src.includes('mapi.losi.one') ||
    alt.toLowerCase().includes('manim') ||
    alt.toLowerCase().includes('math animation')

  // Check if this is a trusted losi.one URL (now has HTTPS, no proxy needed)
  const isLosiUrl =
    src.includes('anim.losi.one') ||
    src.includes('mapi.losi.one') ||
    src.includes('auth.losi.one')

  // For Replicate URLs, we might need to check if they're actually videos
  useEffect(() => {
    const checkReplicateUrl = async () => {
      // If it's a Replicate URL but doesn't have a video extension
      if (src.includes('replicate.delivery') && !src.endsWith('.mp4') && !src.endsWith('.webm')) {
        try {
          // Make a HEAD request to check content type
          const response = await fetch(src, { method: 'HEAD' })
          const contentType = response.headers.get('content-type')

          if (contentType && contentType.includes('video')) {
            // It's a video, continue
            setProcessedSrc(src)
          } else if (!contentType || contentType.includes('application/octet-stream')) {
            // For octet-stream, assume it's a video from replicate
            setProcessedSrc(src)
          } else {
            // Not a video, set error
            setLoadError('This URL does not point to a valid video file.')
          }
        } catch (error) {
          console.error('Error checking Replicate URL:', error)
          // On error, try to load it anyway
          setProcessedSrc(src)
        }
      } else {
        // Use the original source if it has a video extension or doesn't need checking
        setProcessedSrc(src)
      }
    }

    if (!isStreaming && src) {
      checkReplicateUrl()
    }
  }, [isStreaming, src])

  const handleDownload = async () => {
    if (!processedSrc) return
    setIsDownloading(true)
    try {
      // For losi.one URLs, download directly since they now have HTTPS
      if (isLosiUrl) {
        const link = document.createElement('a')
        link.href = processedSrc
        link.download = isGif ? `losi_gif_${Date.now()}.gif` : `losi_video_${Date.now()}.mp4`
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // For other URLs, use the proxy for CORS handling
        const response = await fetch(`/api/video/download?url=${encodeURIComponent(processedSrc)}`)
        if (!response.ok) throw new Error('Network response was not ok')
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = isGif ? `losi_gif_${Date.now()}.gif` : `losi_video_${Date.now()}.mp4`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      }
    } catch (error) {
      console.error('Download failed:', error)
      setLoadError('Failed to download video')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent video controls from activating
    e.preventDefault()
    e.stopPropagation()
    handleDownload()
  }

  const handleVideoError = () => {
    console.error('Video load error for:', processedSrc)
    setLoadError('Failed to load video. It may still be processing or the format is not supported.')
    setVideoLoaded(false)
  }

  const handleImageError = () => {
    console.error('GIF load error for:', processedSrc)
    setLoadError('Failed to load GIF. The format may not be supported.')
    setVideoLoaded(false)
  }

  const handleVideoLoad = () => {
    setVideoLoaded(true)
    setLoadError(null)
  }

  const handleImageLoad = () => {
    setVideoLoaded(true)
    setLoadError(null)
  }

  const handlePlayPause = () => {
    const video = document.querySelector(`video[src="${processedSrc}"]`) as HTMLVideoElement
    if (!video) return

    if (video.paused) {
      video.play()
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  let agent = navigator.userAgent
  let isIphone = agent.indexOf('iPhone') != -1 || agent.indexOf('iPod') != -1
  let isMobile = isIphone || /Android|webOS|iPad|BlackBerry|IEMobile|Opera Mini/i.test(agent)

  // For losi.one URLs, use direct HTTPS URLs since they now support it
  // For other URLs, use proxy for CORS handling
  const finalSrc = isLosiUrl
    ? processedSrc
    : `/api/video/download?url=${encodeURIComponent(processedSrc)}`

  return (
    <div
      className="relative my-4 w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* {!videoLoaded && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50/80 to-gray-100/80 backdrop-blur-sm dark:from-gray-900/80 dark:to-gray-800/80 z-10">
            <ImageLoading />
          </div>
        )} */}

      {loadError ? (
        <div
          className={cn(
            'relative w-full overflow-hidden rounded-lg',
            !videoLoaded && !loadError && 'bg-muted/50'
          )}
        >
          <div className="w-full rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/10 dark:text-red-300">
            <p>{loadError}</p>
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              The video might still be processing or the format is not supported.
            </p>
          </div>
        </div>
      ) : (
        <AnimatePresence>
          <BlurFade duration={0.6} delay={0.2} className="w-full">
            <div className="relative">
              {!videoLoaded && (
                <div className="absolute inset-0 z-10">
                  {isManimAnimation ? (
                    <ManimAnimationSkeleton />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-50/80 to-gray-100/80 backdrop-blur-sm dark:from-gray-900/80 dark:to-gray-800/80">
                      <ImageLoading />
                    </div>
                  )}
                </div>
              )}

              {isGif ? (
                // Render GIF as an image
                <img
                  className={cn(
                    'h-auto w-full rounded-lg object-contain transition-opacity duration-300'
                  )}
                  src={finalSrc}
                  alt={alt || 'Animated GIF'}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              ) : (
                // Render normal video
                <video
                  className={cn(
                    'h-auto w-full rounded-lg object-contain transition-opacity duration-300'
                  )}
                  src={finalSrc}
                  controls
                  playsInline
                  loop
                  muted
                  autoPlay={isIphone}
                  onLoadedData={handleVideoLoad}
                  onError={handleVideoError}
                />
              )}

              {/* Controls overlay for desktop/non-iPhone */}
              {videoLoaded && !isIphone && (
                <div className="absolute right-2 top-2 flex gap-2" style={{ zIndex: 15 }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={isDownloading}
                    disabled={isDownloading}
                    className="size-10 rounded-full bg-black/50 p-0 text-white shadow-lg hover:bg-black/70"
                    onClick={handleButtonClick}
                  >
                    <IconDownload size={18} />
                  </Button>
                </div>
              )}
            </div>

            {/* Separate download button for iPhone */}
            {videoLoaded && isIphone && (
              <div className="mt-2 flex justify-center">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={isDownloading}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-4 py-2"
                  onClick={handleButtonClick}
                >
                  <IconDownload size={16} />
                </Button>
              </div>
            )}
          </BlurFade>
        </AnimatePresence>
      )}

      {/* Custom video player dialog */}
      {showPreview && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="flex h-[90vh] w-[90vw] max-w-4xl items-center justify-center border-none bg-black/90 p-0">
            {isGif ? (
              <img
                src={finalSrc}
                alt={alt || 'Animated GIF'}
                className="max-h-full max-w-full rounded-lg"
              />
            ) : (
              <video
                src={finalSrc}
                className="max-h-full max-w-full rounded-lg"
                controls
                autoPlay
                loop
                playsInline
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
